<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb
        :items="[{ text: 'Reports', path: { name: 'admin-reports' } }]"
        name="Brand Health Scoring"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <b-table
          :fields="reportColumns"
          :items="reportRows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No data found"
      >
        <template #head(brand_name)="data">
          <span
              class="sortable-column"
              @click="toggleSort('brand_name')"
          >
            {{ data.label }} {{ renderSortIcon('brand_name') }}
          </span>
        </template>

        <template #head(integration_status)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('integration_status')"
            >
              {{ data.label }} {{ renderSortIcon('integration_status') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('integration_status')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(live_sku_count)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('live_sku_count')"
            >
              {{ data.label }} {{ renderSortIcon('live_sku_count') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('live_sku_count')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(gl_listing_rate)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('gl_listing_rate')"
            >
              {{ data.label }} {{ renderSortIcon('gl_listing_rate') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('gl_listing_rate')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(order_source_mix)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('order_source_mix')"
            >
              {{ data.label }} {{ renderSortIcon('order_source_mix') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('order_source_mix')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(retail_orders_placed)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('retail_orders_placed')"
            >
              {{ data.label }} {{ renderSortIcon('retail_orders_placed') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('retail_orders_placed')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(wow_order_volume)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('wow_order_volume')"
            >
              {{ data.label }} {{ renderSortIcon('wow_order_volume') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('wow_order_volume')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(inventory_freshness)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('inventory_freshness')"
            >
              {{ data.label }} {{ renderSortIcon('inventory_freshness') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('inventory_freshness')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(time_to_first_value)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('time_to_first_value')"
            >
              {{ data.label }} {{ renderSortIcon('time_to_first_value') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('time_to_first_value')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(multi_user_engagement)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('multi_user_engagement')"
            >
              {{ data.label }} {{ renderSortIcon('multi_user_engagement') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('multi_user_engagement')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(payment_status)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('payment_status')"
            >
              {{ data.label }} {{ renderSortIcon('payment_status') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('payment_status')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(fulfillment_hub_usage)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('fulfillment_hub_usage')"
            >
              {{ data.label }} {{ renderSortIcon('fulfillment_hub_usage') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('fulfillment_hub_usage')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #head(overall_kpi_score)="data">
          <div class="d-inline-flex align-items-center">
            <span
                class="sortable-column"
                @click="toggleSort('overall_kpi_score')"
            >
              {{ data.label }} {{ renderSortIcon('overall_kpi_score') }}
            </span>
            <span
                class="kpi-header-info ml-1 d-inline-flex align-items-center text-muted"
                v-b-tooltip.hover.top
                :title="getKpiHeaderTooltip('overall_kpi_score')"
                @click.stop
            >
              <b-icon-info-circle/>
            </span>
          </div>
        </template>

        <template #cell(integration_status)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.integration_status_info || ''"
          >
            {{ renderStatusIcon(data.item.integration_status) }}
          </span>
        </template>

        <template #cell(live_sku_count)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.live_sku_count_info || ''"
          >
            {{ renderStatusIcon(data.item.live_sku_count) }}
          </span>
        </template>

        <template #cell(gl_listing_rate)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.gl_listing_rate_info || ''"
          >
            {{ renderStatusIcon(data.item.gl_listing_rate) }}
          </span>
        </template>

        <template #cell(order_source_mix)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.order_source_mix_info || ''"
          >
            {{ renderStatusIcon(data.item.order_source_mix) }}
          </span>
        </template>

        <template #cell(retail_orders_placed)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.retail_orders_placed_info || ''"
          >
            {{ renderStatusIcon(data.item.retail_orders_placed) }}
          </span>
        </template>

        <template #cell(wow_order_volume)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.wow_order_volume_info || ''"
          >
            {{ renderStatusIcon(data.item.wow_order_volume) }}
          </span>
        </template>

        <template #cell(inventory_freshness)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.inventory_freshness_info || ''"
          >
            {{ renderStatusIcon(data.item.inventory_freshness) }}
          </span>
        </template>

        <template #cell(time_to_first_value)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.time_to_first_value_info || ''"
          >
            {{ renderStatusIcon(data.item.time_to_first_value) }}
          </span>
        </template>

        <template #cell(multi_user_engagement)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.multi_user_engagement_info || ''"
          >
            {{ renderStatusIcon(data.item.multi_user_engagement) }}
          </span>
        </template>

        <template #cell(payment_status)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.payment_status_info || ''"
          >
            {{ renderStatusIcon(data.item.payment_status) }}
          </span>
        </template>

        <template #cell(fulfillment_hub_usage)="data">
          <span
              v-b-tooltip.hover
              :title="data.item.fulfillment_hub_usage_info || ''"
          >
            {{ renderStatusIcon(data.item.fulfillment_hub_usage) }}
          </span>
        </template>
      </b-table>

      <template v-slot:header>
        <div class="d-flex justify-content-between">
          <div class="d-flex">
            <div class="mr-3">
              <strong class="text-uppercase">Start Date:</strong> {{ startDate }}
              <b-form-datepicker
                  v-model="filters.start_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onStartDateChanged"
              />
            </div>
            <div>
              <strong class="text-uppercase">End Date:</strong> {{ endDate }}
              <b-form-datepicker
                  v-model="filters.end_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onEndDateChanged"
              />
            </div>
          </div>

          <div>
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Download CSV"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>
            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reset Filters"
                @click.prevent="resetFilters"
            >
              <b-icon-arrow-counterclockwise/>
            </b-btn>
          </div>
        </div>
      </template>

      <template v-slot:header-right>
        <div class="mt-n2 d-flex flex-wrap">
          <b-form-select
              class="w-auto mr-2 my-2"
              v-model="filters.health_status"
              :options="healthStatusOptions"
              @change="loadReportData"
          />
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {API_ROUTES} from "../api";

export default {
  name: "AdminReportBrandsHealthScoring",
  components: {AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    const query = this.$route.query;

    return {
      loading: true,
      report_data: [],
      sortableColumns: [
        'brand_name',
        'integration_status',
        'live_sku_count',
        'gl_listing_rate',
        'order_source_mix',
        'retail_orders_placed',
        'wow_order_volume',
        'inventory_freshness',
        'time_to_first_value',
        'multi_user_engagement',
        'payment_status',
        'fulfillment_hub_usage',
        'overall_kpi_score'
      ],
      filters: {
        start_date: query.start_date ?? moment().startOf('month').format('YYYY-MM-DD'),
        end_date: query.end_date ?? moment().format('YYYY-MM-DD'),
        health_status: query.health_status ?? null,
        sort: query.sort ?? 'overall_kpi_score',
        sort_direction: query.sort_direction ?? 'asc',
      },
    };
  },
  methods: {
    setQueryVar(key, value) {
      const query = {...this.$route.query};

      if (value === null || value === '' || typeof value === 'undefined') {
        delete query[key];
      } else {
        query[key] = value;
      }

      this.$router.push({query});
    },
    onStartDateChanged(startDate) {
      if (moment(startDate).isAfter(moment(this.filters.end_date))) {
        this.filters.end_date = startDate;
      }

      this.loadReportData();
    },
    onEndDateChanged(endDate) {
      if (moment(endDate).isBefore(moment(this.filters.start_date))) {
        this.filters.start_date = endDate;
      }

      this.loadReportData();
    },
    toggleSort(field) {
      if (!this.sortableColumns.includes(field)) {
        return;
      }

      if (this.filters.sort === field) {
        this.filters.sort_direction = this.filters.sort_direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.filters.sort = field;
        this.filters.sort_direction = field === 'overall_kpi_score' ? 'asc' : 'desc';
      }

      this.loadReportData();
    },
    renderSortIcon(field) {
      if (this.filters.sort !== field) {
        return '';
      }

      return this.filters.sort_direction === 'asc' ? '↑' : '↓';
    },
    loadReportData() {
      const params = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        health_status: this.filters.health_status,
        sort: this.filters.sort,
        sort_direction: this.filters.sort_direction,
      };

      this.loading = true;

      this.$api.get(API_ROUTES.ADMIN_REPORT_BRANDS_HEALTH_SCORING, {params})
          .then(({data}) => {
            this.report_data = Array.isArray(data.report_data) ? data.report_data : [];
          })
          .catch(({response}) => {
            let message = 'Failed to load report';

            if (response && response.status === 422) {
              message = 'Some of your params were incorrect';
              this.resetFilters();
            }

            this.$bvToast.toast(message, {
              title: 'Error!',
              variant: 'danger'
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    resetFilters() {
      this.filters = {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        health_status: null,
        sort: 'overall_kpi_score',
        sort_direction: 'asc',
      };

      this.loadReportData();
    },
    renderStatusIcon(status) {
      const normalized = String(status || '').toLowerCase();

      if (normalized === 'green') {
        return '🟢';
      }

      if (normalized === 'amber') {
        return '🟡';
      }

      if (normalized === 'red') {
        return '🔴';
      }

      if (normalized === 'not_ready') {
        return 'N/A';
      }

      return '';
    },
    getStatusWeight(status) {
      const normalized = String(status || '').toLowerCase();

      if (normalized === 'green') {
        return 2;
      }

      if (normalized === 'amber') {
        return 1;
      }

      if (normalized === 'red') {
        return 0;
      }

      return 0;
    },
    calculateOverallScore(statuses) {
      const weights = {
        green: 2,
        amber: 1,
        red: 0,
      };

      return statuses.reduce((sum, status) => {
        if (!(status in weights)) {
          return sum;
        }

        return sum + weights[status];
      }, 0);
    },
    calculateHealthStatus(statuses) {
      if (!statuses.length) {
        return 'red';
      }

      const totalScore = this.calculateOverallScore(statuses);
      const maxScore = statuses.length * 2;
      const ratio = totalScore / maxScore;

      if (ratio >= 0.75) {
        return 'green';
      }

      if (ratio >= 0.4) {
        return 'amber';
      }

      return 'red';
    },
    getKpiHeaderTooltip(key) {
      const tooltips = {
        integration_status: 'Status of brand integrations like METRC, LeafLink, QBO and similar systems.',
        live_sku_count: 'Number of live SKUs available for this brand.',
        gl_listing_rate: 'Share of the brand catalog currently listed on GL.',
        order_source_mix: 'How much of order activity comes through GL versus other sources.',
        retail_orders_placed: 'Retail order activity for the selected period.',
        wow_order_volume: 'Week-over-week order volume trend.',
        inventory_freshness: 'How recently inventory data was updated.',
        time_to_first_value: 'How quickly the brand reached first order value.',
        multi_user_engagement: 'Level of activity from multiple users on the account.',
        payment_status: 'Current payment standing for the brand.',
        fulfillment_hub_usage: 'Whether and how actively Fulfillment Hub is being used.',
        overall_kpi_score: 'Combined score based on all implemented KPI statuses.',
      };

      return tooltips[key] || '';
    },
    downloadCsv() {
      if (!this.reportRows.length) {
        return;
      }

      const escapeCsvValue = (value) => {
        const normalized = String(value ?? '')
            .replace(/\r\n/g, ' ')
            .replace(/\n/g, ' ')
            .replace(/\r/g, ' ')
            .replace(/"/g, '""')
            .trim();

        return `"${normalized}"`;
      };

      const formatKpi = (status, info) => {
        const normalizedStatus = String(status || '').toLowerCase();

        if (normalizedStatus === 'not_ready' || !normalizedStatus) {
          return 'N/A';
        }

        const labelMap = {
          green: 'Green',
          amber: 'Amber',
          red: 'Red',
        };

        const label = labelMap[normalizedStatus] || normalizedStatus;
        const normalizedInfo = String(info || '')
            .replace(/\r\n/g, ' ')
            .replace(/\n/g, ' ')
            .replace(/\r/g, ' ')
            .trim();

        if (!normalizedInfo) {
          return label;
        }

        return `${label} (${normalizedInfo})`;
      };

      const headers = [
        'Brand Name',
        'Health Status',
        'Integration Status',
        'Live SKU Count',
        'GL Listing Rate',
        'Order Source Mix',
        'Retail Orders Placed',
        'WoW Order Volume',
        'Inventory Freshness',
        'Time to First Value',
        'Multi-User Engagement',
        'Payment Status',
        'Fulfillment Hub Usage',
        'Overall KPI Score'
      ];

      const rows = this.reportRows.map((row) => [
        escapeCsvValue(row.brand_name),
        escapeCsvValue(row.health_status),
        escapeCsvValue(formatKpi(row.integration_status, row.integration_status_info)),
        escapeCsvValue(formatKpi(row.live_sku_count, row.live_sku_count_info)),
        escapeCsvValue(formatKpi(row.gl_listing_rate, row.gl_listing_rate_info)),
        escapeCsvValue(formatKpi(row.order_source_mix, row.order_source_mix_info)),
        escapeCsvValue(formatKpi(row.retail_orders_placed, row.retail_orders_placed_info)),
        escapeCsvValue(formatKpi(row.wow_order_volume, row.wow_order_volume_info)),
        escapeCsvValue(formatKpi(row.inventory_freshness, row.inventory_freshness_info)),
        escapeCsvValue(formatKpi(row.time_to_first_value, row.time_to_first_value_info)),
        escapeCsvValue(formatKpi(row.multi_user_engagement, row.multi_user_engagement_info)),
        escapeCsvValue(formatKpi(row.payment_status, row.payment_status_info)),
        escapeCsvValue(formatKpi(row.fulfillment_hub_usage, row.fulfillment_hub_usage_info)),
        escapeCsvValue(row.overall_kpi_score ?? 0),
      ]);

      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...rows.map((row) => row.join(','))
      ].join('\r\n');

      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.setAttribute('href', url);
      link.setAttribute('download', 'brand_health_scoring.csv');

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },
  },
  computed: {
    pageTitle() {
      return 'Brand Health Scoring';
    },
    maxDate() {
      return moment().format('YYYY-MM-DD');
    },
    startDate() {
      return moment(this.filters.start_date).format('L');
    },
    endDate() {
      return moment(this.filters.end_date).format('L');
    },
    healthStatusOptions() {
      return [
        {value: null, text: 'All Health Statuses'},
        {value: 'red', text: 'Red'},
        {value: 'amber', text: 'Amber'},
        {value: 'green', text: 'Green'},
      ];
    },
    reportColumns() {
      return [
        {
          key: "brand_name",
          label: "Brand Name",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "integration_status",
          label: "Integration",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "live_sku_count",
          label: "SKU",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "gl_listing_rate",
          label: "GL Listing",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "order_source_mix",
          label: "Order Mix",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "retail_orders_placed",
          label: "Retail Orders",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "wow_order_volume",
          label: "WoW Volume",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "inventory_freshness",
          label: "Freshness",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "time_to_first_value",
          label: "Time to Value",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "multi_user_engagement",
          label: "Engagement",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "payment_status",
          label: "Payment",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "fulfillment_hub_usage",
          label: "Fulfillment",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        },
        {
          key: "overall_kpi_score",
          label: "Overall KPI Score",
          thClass: "border-top-0",
          tdClass: "align-middle text-center"
        }
      ];
    },
    reportRows() {
      if (!Array.isArray(this.report_data)) {
        return [];
      }

      return this.report_data.map((item) => {
        const rawStatuses = {
          integration_status: String(item.integration_status ?? '').toLowerCase(),
          live_sku_count: String(item.live_sku_count ?? '').toLowerCase(),
          gl_listing_rate: String(item.gl_listing_rate ?? '').toLowerCase(),
          order_source_mix: String(item.order_source_mix ?? '').toLowerCase(),
          retail_orders_placed: String(item.retail_orders_placed ?? '').toLowerCase(),
          wow_order_volume: String(item.wow_order_volume ?? '').toLowerCase(),
          inventory_freshness: String(item.inventory_freshness ?? '').toLowerCase(),
          time_to_first_value: String(item.time_to_first_value ?? '').toLowerCase(),
          multi_user_engagement: String(item.multi_user_engagement ?? '').toLowerCase(),
          payment_status: String(item.payment_status ?? '').toLowerCase(),
          fulfillment_hub_usage: String(item.fulfillment_hub_usage ?? '').toLowerCase(),
        };

        const statuses = Object.values(rawStatuses);

        return {
          brand_name: item.brand_name ?? 'Unknown',

          integration_status: rawStatuses.integration_status,
          integration_status_info: item.integration_status_info ?? '',

          live_sku_count: rawStatuses.live_sku_count,
          live_sku_count_info: item.live_sku_count_info ?? '',

          gl_listing_rate: rawStatuses.gl_listing_rate,
          gl_listing_rate_info: item.gl_listing_rate_info ?? '',

          order_source_mix: rawStatuses.order_source_mix,
          order_source_mix_info: item.order_source_mix_info ?? '',

          retail_orders_placed: rawStatuses.retail_orders_placed,
          retail_orders_placed_info: item.retail_orders_placed_info ?? '',

          wow_order_volume: rawStatuses.wow_order_volume,
          wow_order_volume_info: item.wow_order_volume_info ?? '',

          inventory_freshness: rawStatuses.inventory_freshness,
          inventory_freshness_info: item.inventory_freshness_info ?? '',

          time_to_first_value: rawStatuses.time_to_first_value,
          time_to_first_value_info: item.time_to_first_value_info ?? '',

          multi_user_engagement: rawStatuses.multi_user_engagement,
          multi_user_engagement_info: item.multi_user_engagement_info ?? '',

          payment_status: rawStatuses.payment_status,
          payment_status_info: item.payment_status_info ?? '',

          fulfillment_hub_usage: rawStatuses.fulfillment_hub_usage,
          fulfillment_hub_usage_info: item.fulfillment_hub_usage_info ?? '',

          overall_kpi_score: Number(item.overall_kpi_score ?? this.calculateOverallScore(statuses)),
          health_status: String(item.health_status ?? this.calculateHealthStatus(statuses)).toLowerCase(),
        };
      });
    }
  },
  watch: {
    "filters.start_date": function (startDate) {
      if (startDate === moment().startOf('month').format('YYYY-MM-DD')) {
        startDate = null;
      }

      this.setQueryVar('start_date', startDate);
    },
    "filters.end_date": function (endDate) {
      if (endDate === moment().format('YYYY-MM-DD')) {
        endDate = null;
      }

      this.setQueryVar('end_date', endDate);
    },
    "filters.health_status": function (healthStatus) {
      this.setQueryVar('health_status', healthStatus);
    },
    "filters.sort": function (sort) {
      this.setQueryVar('sort', sort);
    },
    "filters.sort_direction": function (sortDirection) {
      this.setQueryVar('sort_direction', sortDirection);
    },
  },
  created() {
    this.loadReportData();
  }
}
</script>

<style scoped>
.kpi-header-info {
  font-size: 11px;
  cursor: help;
  line-height: 1;
}
</style>